import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Box,
  Grid,
  Avatar,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  LinearProgress,
  IconButton,
  Divider,
  useTheme,
  Snackbar
} from '@mui/material';
import {
  Link as LinkIcon,
  LinkOff,
  Sync,
  Settings,
  CheckCircle,
  Error as ErrorIcon,
  Refresh,
  Info
} from '@mui/icons-material';
import { useDevices } from '../../hooks/useDevices';

const DeviceConnectionCardV2 = ({ className, style }) => {
  const theme = useTheme();
  const {
    devices,
    loading,
    error,
    syncing,
    connectedDevices,
    connectionPercentage,
    fetchDevices,
    connectFitbit,
    connectGarmin,
    syncDevice,
    disconnectDevice,
    getDeviceStatus,
    getStatusText,
    setError
  } = useDevices();

  const [garminModal, setGarminModal] = useState(false);
  const [garminCredentials, setGarminCredentials] = useState({ username: '', password: '' });
  const [actionLoading, setActionLoading] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  const showMessage = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleFitbitConnect = async () => {
    setActionLoading({ fitbit: 'connecting' });
    try {
      await connectFitbit();
      showMessage('Fitbit connected successfully!', 'success');
    } catch (error) {
      showMessage('Failed to connect Fitbit', 'error');
    } finally {
      setActionLoading({});
    }
  };

  const handleGarminConnect = async () => {
    setActionLoading({ garmin: 'connecting' });
    try {
      await connectGarmin(garminCredentials);
      showMessage('Garmin connected successfully!', 'success');
      setGarminModal(false);
      setGarminCredentials({ username: '', password: '' });
    } catch (error) {
      showMessage('Failed to connect Garmin. Please check your credentials.', 'error');
    } finally {
      setActionLoading({});
    }
  };

  const handleDeviceDisconnect = async (deviceType) => {
    if (!window.confirm(`Disconnect ${deviceType.charAt(0).toUpperCase() + deviceType.slice(1)}? This will remove your device connection and stop syncing step data.`)) {
      return;
    }

    setActionLoading({ [deviceType]: 'disconnecting' });
    try {
      await disconnectDevice(deviceType);
      showMessage(`${deviceType.charAt(0).toUpperCase() + deviceType.slice(1)} disconnected successfully`, 'success');
    } catch (error) {
      showMessage(`Failed to disconnect ${deviceType}`, 'error');
    } finally {
      setActionLoading({});
    }
  };

  const handleManualSync = async (deviceType) => {
    setActionLoading({ [deviceType]: 'syncing' });
    try {
      const response = await syncDevice(deviceType);
      showMessage(`${deviceType.charAt(0).toUpperCase() + deviceType.slice(1)} sync completed! Synced ${response.synced_days?.length || 0} days of data.`, 'success');
    } catch (error) {
      showMessage(`Failed to sync ${deviceType} data`, 'error');
    } finally {
      setActionLoading({});
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'connected': return 'success';
      case 'error': return 'error';
      default: return 'default';
    }
  };

  const getDeviceIcon = (deviceType) => {
    const iconProps = {
      sx: { 
        width: 40, 
        height: 40,
        bgcolor: deviceType === 'fitbit' ? '#00B0B9' : '#007cc3',
        color: 'white'
      }
    };
    
    return (
      <Avatar {...iconProps}>
        {deviceType.charAt(0).toUpperCase()}
      </Avatar>
    );
  };

  const renderDeviceCard = (device) => {
    const status = getDeviceStatus(device);
    const isLoading = actionLoading[device.device_type];
    const isSyncing = syncing.has(device.device_type);

    return (
      <Grid item xs={12} lg={6} key={device.device_type}>
        <Card 
          sx={{ 
            height: '100%',
            borderLeft: `4px solid ${
              status === 'connected' ? theme.palette.success.main :
              status === 'error' ? theme.palette.error.main :
              theme.palette.grey[300]
            }`,
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <CardContent sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              {getDeviceIcon(device.device_type)}
              <Box sx={{ ml: 2, flexGrow: 1 }}>
                <Typography variant="h6" component="div">
                  {device.device_type.charAt(0).toUpperCase() + device.device_type.slice(1)}
                </Typography>
                <Chip 
                  label={getStatusText(device)}
                  color={getStatusColor(status)}
                  size="small"
                  icon={status === 'connected' ? <CheckCircle /> : status === 'error' ? <ErrorIcon /> : <Info />}
                />
              </Box>
            </Box>

            {device.connected ? (
              <Box sx={{ mt: 2 }}>
                <Grid container spacing={1} sx={{ mb: 1 }}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Connected:
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      {new Date(device.connection_info?.connected_at || device.connected_at).toLocaleDateString()}
                    </Typography>
                  </Grid>
                </Grid>
                
                {(device.connection_info?.last_sync || device.last_sync) && (
                  <Grid container spacing={1} sx={{ mb: 1 }}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Last Sync:
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2">
                        {new Date(device.connection_info?.last_sync || device.last_sync).toLocaleString()}
                      </Typography>
                    </Grid>
                  </Grid>
                )}

                {device.device_type === 'fitbit' && device.connection_info?.device_specific?.token_valid === false && (
                  <Alert severity="warning" size="small" sx={{ mt: 1 }}>
                    Token expired. Please reconnect to continue syncing data.
                  </Alert>
                )}

                {status === 'connected' && (
                  <Box sx={{ mt: 1 }}>
                    <Chip 
                      label="Auto-sync enabled" 
                      color="success" 
                      size="small"
                      icon={<CheckCircle />}
                    />
                  </Box>
                )}
              </Box>
            ) : (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Connect your {device.device_type} device to automatically sync your step data for challenges.
                </Typography>
                
                {device.device_type === 'fitbit' && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    We use Fitbit's official OAuth 2.0 for secure authentication. Your credentials are never stored.
                  </Alert>
                )}
                
                {device.device_type === 'garmin' && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    Connect using your Garmin Connect credentials to sync activity data.
                  </Alert>
                )}
              </Box>
            )}
          </CardContent>

          <Divider />
          
          <CardActions sx={{ justifyContent: 'flex-end', gap: 1 }}>
            {device.connected ? (
              <>
                <Button
                  size="small"
                  startIcon={<Sync />}
                  onClick={() => handleManualSync(device.device_type)}
                  disabled={isLoading === 'syncing' || isSyncing}
                  variant="contained"
                >
                  {isLoading === 'syncing' || isSyncing ? 'Syncing...' : 'Sync Now'}
                </Button>
                <IconButton size="small" title="Settings">
                  <Settings />
                </IconButton>
                <Button
                  size="small"
                  startIcon={<LinkOff />}
                  onClick={() => handleDeviceDisconnect(device.device_type)}
                  disabled={isLoading === 'disconnecting'}
                  color="error"
                >
                  {isLoading === 'disconnecting' ? 'Disconnecting...' : 'Disconnect'}
                </Button>
              </>
            ) : (
              <Button
                size="small"
                startIcon={<LinkIcon />}
                onClick={() => {
                  if (device.device_type === 'fitbit') {
                    handleFitbitConnect();
                  } else if (device.device_type === 'garmin') {
                    setGarminModal(true);
                  }
                }}
                disabled={isLoading === 'connecting'}
                variant="contained"
              >
                {isLoading === 'connecting' ? 'Connecting...' : 'Connect'}
              </Button>
            )}
          </CardActions>
        </Card>
      </Grid>
    );
  };

  if (loading) {
    return (
      <Card className={className} style={style}>
        <CardContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
            <CircularProgress size={40} />
            <Typography variant="body2" sx={{ mt: 2 }}>
              Loading device connections...
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box className={className} style={style}>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" component="h2">
              Device Connections
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {connectedDevices.length} of {devices.length} connected
              </Typography>
              <IconButton onClick={fetchDevices} disabled={loading}>
                <Refresh />
              </IconButton>
            </Box>
          </Box>

          {/* Connection Overview */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6">Connection Status</Typography>
              <Typography variant="body2" color="text.secondary">
                {connectionPercentage}%
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={connectionPercentage}
              color={connectedDevices.length === devices.length ? 'success' : 'primary'}
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Box>
          
          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          
          {connectedDevices.length === 0 && !error && (
            <Alert severity="info" sx={{ mb: 3 }}>
              No devices connected. Connect your fitness devices to automatically sync step data for challenges. 
              Data syncing will begin once you join an active challenge.
            </Alert>
          )}

          <Grid container spacing={2}>
            {devices.map(renderDeviceCard)}
          </Grid>
        </CardContent>
      </Card>

      {/* Snackbar for messages */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Garmin Connection Dialog */}
      <Dialog
        open={garminModal}
        onClose={() => {
          setGarminModal(false);
          setGarminCredentials({ username: '', password: '' });
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Connect Garmin</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 3 }}>
            Your Garmin Connect credentials are encrypted and stored securely. 
            We only access your step data for challenge participation.
          </Alert>
          
          <TextField
            fullWidth
            label="Garmin Connect Username/Email"
            value={garminCredentials.username}
            onChange={(e) => setGarminCredentials({ ...garminCredentials, username: e.target.value })}
            margin="normal"
            required
          />
          
          <TextField
            fullWidth
            label="Password"
            type="password"
            value={garminCredentials.password}
            onChange={(e) => setGarminCredentials({ ...garminCredentials, password: e.target.value })}
            margin="normal"
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGarminModal(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleGarminConnect}
            variant="contained"
            disabled={actionLoading.garmin === 'connecting' || !garminCredentials.username || !garminCredentials.password}
          >
            {actionLoading.garmin === 'connecting' ? 'Connecting...' : 'Connect Garmin'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DeviceConnectionCardV2;